import { useState, useCallback } from "react";

/**
 * useValidationErrors 是一個自訂 Hook，
 * 用於處理表單驗證錯誤的解析、更新與清除，支援巢狀物件結構。
 *
 * @returns {Object} 包含以下屬性與方法：
 * - validationErrors：一個物件，儲存已格式化的驗證錯誤訊息
 * - updateValidationErrors(errorsArray)：更新驗證錯誤，接收後端錯誤陣列
 * - clearValidationErrors()：清除所有驗證錯誤
 *
 * @example
 * const {
 *   validationErrors,
 *   updateValidationErrors,
 *   clearValidationErrors
 * } = useValidationErrors();
 *
 * updateValidationErrors([
 *   { key: 'users[0].email', message: 'Email 為必填欄位' }
 * ]);
 *
 * console.log(validationErrors.users[0].email); // 'Email 為必填欄位'
 */
const useValidationErrors = () => {
	const [validationErrors, setValidationErrors] = useState({});

	/**
	 * 將後端錯誤陣列解析為巢狀物件結構
	 *
	 * @param {Array} array - 錯誤陣列，每筆包含 `key` 和 `message`
	 * @returns {Object} 轉換後的錯誤物件
	 */
	const parseValidationErrors = useCallback(
		array => {
			const result = {};
			if (Array.isArray(array)) {
				array.forEach(item => {
					// 去掉方括號與數字（保留數字但去除中括號）
					const cleanedKey = item.key.replace(/\[(\d+)\]/g, "$1");
					const keys = cleanedKey.split(".");
					let obj = result;
					keys.forEach((key, index) => {
						if (index === keys.length - 1) {
							obj[key] = item.message;
						} else {
							obj[key] = obj[key] || {};
							obj = obj[key];
						}
					});
				});
			}
			return result;
		},
		// eslint-disable-next-line
		[]
	);

	/**
	 * 更新驗證錯誤狀態
	 *
	 * @param {Array} errorsArray - 從後端接收的錯誤陣列
	 */
	const updateValidationErrors = useCallback(
		errorsArray => {
			const formattedErrors = parseValidationErrors(errorsArray);
			setValidationErrors(formattedErrors);
		},
		[parseValidationErrors]
	);

	/**
	 * 清除所有驗證錯誤
	 */
	const clearValidationErrors = useCallback(() => {
		setValidationErrors({});
	}, []);

	return {
		validationErrors,
		updateValidationErrors,
		clearValidationErrors,
	};
};

export default useValidationErrors;
